import React, { useRef, useState } from "react";
import { Box, Button, Flex, Select, Text } from "@chakra-ui/react";
import { agentGet } from "../../utils/apis/agentApi";

const ms = (value: unknown) => typeof value === "number" && Number.isFinite(value) ? `${value.toFixed(3)} ms` : "Not measured";

export default function JevTiming() {
  const [open, setOpen] = useState(false);
  const [hours, setHours] = useState("24");
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const generation = useRef(0);
  const load = async (window = hours) => {
    const id = ++generation.current;
    setBusy(true); setError(false); setData(null);
    try {
      const result = await agentGet(`api/jev/timing?hours=${window}`);
      if (id !== generation.current) return;
      if (!result.ok || !Array.isArray(result.data?.baseline)) throw new Error("unavailable");
      setData(result.data);
    } catch { if (id === generation.current) setError(true); }
    finally { if (id === generation.current) setBusy(false); }
  };
  return <Box my={4} borderWidth="1px" borderRadius="md" p={3} minW={0}>
    <Button variant="outline" aria-expanded={open} onClick={() => {
      setOpen(v => !v);
      if (!open) void load();
      else { generation.current += 1; setBusy(false); }
    }}>Jev timing comparison</Button>
    {open && <Box mt={3}>
      <Text fontSize="sm">Compare the same task and implementation. A shadow model call does not replace the current workflow or measure a customer speed-up.</Text>
      <Flex gap={2} my={3} wrap="wrap">
        <Select maxW="180px" value={hours} aria-label="Timing window" onChange={e => { setHours(e.target.value); void load(e.target.value); }}>
          <option value="24">Last 24 hours</option><option value="168">Last 7 days</option>
        </Select>
        <Button isDisabled={busy} onClick={() => void load()}>Refresh timings</Button>
      </Flex>
      {busy && <Text role="status">Loading timings…</Text>}
      {error && <Text role="alert">Timings unavailable. Check that the timing API has been deployed, then refresh.</Text>}
      {data && <>
        {data.campaign && <Text fontSize="sm" mb={3}>
          Campaign: {data.campaign.active ? "active" : "inactive"}. Reserved: ${Number(data.campaign.reserved_usd || 0).toFixed(6)} of $10 total; $2 rolling daily cap. Reported charges: ${Number(data.campaign.reported_cost_usd || 0).toFixed(8)}.
          {data.campaign.expires_at && <> Shadow ends: {new Date(data.campaign.expires_at).toLocaleString()}.</>}
        </Text>}
        <Text fontWeight="bold">Task timings: existing implementation</Text>
        {data.baseline.length === 0 ? <Text>No task measurements yet.</Text> : <Box overflowX="auto" mt={2}>
          <table style={{ width: "100%", minWidth: 850, borderCollapse: "collapse" }}>
            <thead><tr>{["Task / scope", "Implementation", "Channel / language", "Build", "Outcome", "Samples", "Median", "p95", "p99"].map(h => <th key={h} style={{ textAlign:"left", padding:8 }}>{h}</th>)}</tr></thead>
            <tbody>{data.baseline.map((row: any, i: number) => <tr key={i}>
              <td>{row.stage.replace(/_/g," ")} / {row.scope}</td><td>{row.implementation}</td>
              <td>{row.channel} / {row.language}</td><td>{row.build}</td><td>{row.outcome}</td>
              <td>{row.samples}</td><td>{ms(row.p50_ms)}</td><td>{ms(row.p95_ms)}</td><td>{ms(row.p99_ms)}</td>
            </tr>)}</tbody>
          </table>
        </Box>}
        {(data.candidate || []).length === 0 ? <Text mt={3}>Jev comparison: no paired production measurements yet. No speed improvement has been established.</Text> : <Box mt={4}>
          <Text fontWeight="bold">Paired Jev shadow comparison</Text>
          <Text fontSize="sm">Same eligible input, existing decision unchanged. Positive delta means Jev took longer. Agreement with the existing system is not proof of correctness. Errors and abstentions remain visible.</Text>
          <Box overflowX="auto" mt={2}><table style={{width:"100%",minWidth:1100}}>
            <thead><tr>{["Task / baseline", "Channel / language / build", "Completed / pending / errors", "Existing median / p95", "Jev median / p95 / p99", "Paired delta median", "Abstentions", "Agree / disagree", "Vendor / queue p95", "Reported cost"].map(h => <th key={h} style={{textAlign:"left",padding:8}}>{h}</th>)}</tr></thead>
            <tbody>{data.candidate.map((row:any,i:number) => <tr key={i}>
              <td>{row.stage.replace(/_/g," ")} / {row.implementation}</td><td>{row.channel} / {row.language} / {row.build}</td>
              <td>{row.completed} / {row.pending} / {row.errors}</td><td>{ms(row.baseline_p50_ms)} / {ms(row.baseline_p95_ms)}</td>
              <td>{ms(row.candidate_p50_ms)} / {ms(row.candidate_p95_ms)} / {ms(row.candidate_p99_ms)}</td><td>{ms(row.delta_p50_ms)}</td>
              <td>{row.abstentions}</td><td>{row.agreements} / {row.disagreements}</td><td>{ms(row.vendor_p95_ms)} / {ms(row.queue_p95_ms)}</td>
              <td>${Number(row.reported_cost_usd || 0).toFixed(8)}</td>
            </tr>)}</tbody>
          </table></Box>
        </Box>}
        <Text fontWeight="bold" mt={4}>Customer turn timings by deployed build</Text>
        <Text fontSize="sm">Traffic and task mix may differ between builds. These totals are not a controlled comparison.</Text>
        {(data.turns_by_build || []).length === 0 ? <Text>No customer turn measurements in this window.</Text> : <Box overflowX="auto" mt={2}>
          <table style={{ width:"100%", minWidth:650, borderCollapse:"collapse" }}>
            <thead><tr>{["Build", "Channel", "Turns", "Median", "p95", "Queue p95", "Brain p95"].map(h => <th key={h} style={{ textAlign:"left", padding:8 }}>{h}</th>)}</tr></thead>
            <tbody>{data.turns_by_build.map((row:any,i:number) => <tr key={i}>
              <td>{row.build}</td><td>{row.channel}</td><td>{row.turns}</td><td>{ms(row.p50_ms)}</td>
              <td>{ms(row.p95_ms)}</td><td>{ms(row.queue_p95_ms)}</td><td>{ms(row.brain_p95_ms)}</td>
            </tr>)}</tbody>
          </table>
        </Box>}
      </>}
    </Box>}
  </Box>;
}
